import { useCallback, useMemo } from 'react';
import { useBranch } from '../context/BranchContext';

/**
 * Hook để tự động thêm branch_id vào API calls
 * @returns {Function} Function để wrap API calls với branch_id
 */
export const useBranchAPI = () => {
  const { selectedBranch, isBranchSelected } = useBranch();

  /**
   * Wrap API call với branch_id
   * @param {Function} apiCall - API function to call
   * @param {Object} params - Parameters for API call
   * @returns {Promise} API response
   */
  const withBranch = useCallback(
    async (apiCall, params = {}) => {
      if (!isBranchSelected()) {
        throw new Error('Vui lòng chọn chi nhánh trước khi thực hiện thao tác');
      }

      const paramsWithBranch = {
        ...params,
        branch_id: selectedBranch.id,
      };

      return await apiCall(paramsWithBranch);
    },
    [selectedBranch?.id, isBranchSelected],
  );

  /**
   * Wrap API call với branch_id trong data
   * @param {Function} apiCall - API function to call
   * @param {Object} data - Data for API call
   * @returns {Promise} API response
   */
  const withBranchData = useCallback(
    async (apiCall, data = {}) => {
      if (!isBranchSelected()) {
        throw new Error('Vui lòng chọn chi nhánh trước khi thực hiện thao tác');
      }

      const dataWithBranch = {
        ...data,
        branch_id: selectedBranch.id,
      };

      return await apiCall(dataWithBranch);
    },
    [selectedBranch?.id, isBranchSelected],
  );

  /**
   * Lấy branch_id hiện tại
   * @returns {string|null} Branch ID
   */
  const getBranchId = useCallback(() => {
    return selectedBranch?.id || null;
  }, [selectedBranch?.id]);

  /**
   * Lấy thông tin branch hiện tại
   * @returns {Object|null} Branch object
   */
  const getBranch = useCallback(() => {
    return selectedBranch;
  }, [selectedBranch]);

  return useMemo(
    () => ({
      withBranch,
      withBranchData,
      getBranchId,
      getBranch,
      isBranchSelected,
      selectedBranch,
    }),
    [withBranch, withBranchData, getBranchId, getBranch, isBranchSelected, selectedBranch],
  );
};

/**
 * Utility function để thêm branch_id vào params
 * @param {Object} params - Original params
 * @param {string} branchId - Branch ID
 * @returns {Object} Params with branch_id
 */
export const addBranchToParams = (params, branchId) => {
  if (!branchId) return params;

  return {
    ...params,
    branch_id: branchId,
  };
};

/**
 * Utility function để thêm branch_id vào data
 * @param {Object} data - Original data
 * @param {string} branchId - Branch ID
 * @returns {Object} Data with branch_id
 */
export const addBranchToData = (data, branchId) => {
  if (!branchId) return data;

  return {
    ...data,
    branch_id: branchId,
  };
};
